import React, { useMemo, useEffect, useRef, useCallback } from "react";
import styles from "./PanelContainer.module.scss";
import { getProjectionList } from "config/panelConfig";
import { useAnalysisPage } from "@features/analysis/useAnalysisPage";
import { usePanelContainer } from "./usePanelContainer";
import {
  addCornerstoneCoreEvents,
  checkLabelmap,
  downloadQueueByType,
  fitDicomViewportsToScreen,
  removeCornerstoneCoreEvents,
  resizeEngine,
  volumeCacheVolumeAddedEvent,
} from "./Renderers/CornerstoneManager";
import { useBrowserPage } from "@features/browser/useBrowserPage";
import useAuth from "@hooks/Auth/useAuth";
import {
  arrangeBranchDatasetWorker,
  arrangeLabelDataWorker,
  ensureMainBranchIds,
  getSegmentationBufferKeys,
  handleParameter,
  requestCPRData,
  requestDraco,
  requestBufferPoolJson,
  setupOwnPool,
  splitAortaValveCalciumDataWorker,
  splitTaviDataWorker,
  splitBaseCenterDataWorker,
  useBufferPool,
  requestTerrData,
  requestMappingData,
  processOriginalData,
  applyParameterPaths,
  TAVI_RESOURCE_KEYS,
  requestCprCrossSectionImages,
  requestPolyDataModel,
} from "./Renderers/vtkManagers";
import { useBranchInfo } from "@contexts/BranchContext";
import { IndexedStates } from "@features/analysis/AnalysisPageSlice";
import { getPanel } from "../panelRegistry";
import {
  AnalysisType,
  EditNames,
  getOffSegmentation,
  getSegmentationAnalysisType,
  SegmentationProps,
  SegmentationControllerType,
} from "config/analysisConfig";
import { useConfig } from "config/useConfig";
import Modal from "@components/Modal";
import { useModal } from "@components/Modal/ModalProvider";
import ResponseWaiting from "@components/ResponseWaiting/ResponseWaiting";

import vtkPolyData from "@kitware/vtk.js/Common/DataModel/PolyData";
import { renderers, scheduleRender } from "./renderState";
import {
  BufferData,
  captureSurface3D,
  imageUrlToArrayBufferSimple,
  screenshotElement,
} from "utils/captureUtils";
import {
  Send_PACS_Data,
  useAutoCaptureModal,
} from "@features/analysis/analysisModal/AutoCaptureModal/AutoCaptureModal";
import { useTool } from "@hooks/Tool/useTool";
import { calculateRRPercent } from "@hooks/useAnalysisForPanel/useDicomUtils";
import FfrCaptureBar, { CaptureThumb } from "./FfrCaptureBar";
import { FFRNames } from "config/render3DConfig";
import {
  getConfiguredCprCrossSectionImageManifestTarget,
  getConfiguredCprCrossSectionImageMode,
} from "./cprImageModeConfig";
import {
  getEffectiveCprCrossSectionImageMode,
  getCprCrossSectionImageManifestStatus,
  isCprCrossSectionMainRenderReady,
  isCprCrossSectionImageShellReady,
  shouldUseCprCrossSectionImageShellForLayout,
  shouldRequestCprCrossSectionImages,
  shouldForceLegacyCprForFfrLumenEdit,
} from "./cprImageModeState";

type LayoutSpec = {
  slots: string[];
  columns?: string;
  rows?: string;
  areas?: string[];
};

const LAYOUT_REGISTRY: Record<string, LayoutSpec> = {
  ffr: {
    slots: ["right", "left1", "left2"],
    columns: "2fr 1fr",
    rows: "repeat(2, 1fr)",
    areas: ["right left1", "right left2"],
  },
  "ffr-capture": {
    slots: ["right", "left1", "left2", "bottom-left"],
    columns: "2fr 1fr",
    rows: "repeat(8, 1fr)",
    areas: [
      "right left1",
      "right left1",
      "right left1",
      "right left1",
      "right left2",
      "right left2",
      "right left2",
      "bottom-left left2",
    ],
  },
  "ffr-1-2-2": {
    slots: ["CPR-0-S", "top1", "top2", "bottom1", "bottom2"],
    columns: "1fr 1fr 1fr",
    rows: "repeat(2, 1fr)",
    areas: ["CPR-0-S top1 top2", "CPR-0-S bottom1 bottom2"],
  },
  "1": {
    slots: [""],
    columns: "repeat(1, minmax(0, 1fr))",
    rows: "repeat(1, minmax(0, 1fr))",
  },
  "1-1": {
    slots: ["left", "right"],
    columns: "repeat(2, 1fr)",
    areas: ["left right"],
  },
  "1-2": {
    slots: ["left", "right1", "right2"],
    columns: "1fr 1fr",
    rows: "repeat(2, 1fr)",
    areas: ["left right1", "left right2"],
  },
  "1-3": {
    slots: ["left", "right1", "right2", "right3"],
    columns: "1fr 1fr",
    rows: "repeat(3, 1fr)",
    areas: ["left right1", "left right2", "left right3"],
  },
  "2-1": {
    slots: ["left1", "left2", "right"],
    columns: "1fr 2fr",
    rows: "repeat(2, 1fr)",
    areas: ["left1 right", "left2 right"],
  },
  "3-1": {
    slots: ["left1", "left2", "left3", "right"],
  },
  "1_1": {
    slots: ["top", "bottom"],
    rows: "repeat(2, 1fr)",
    areas: ["top", "bottom"],
  },
  "1_2": {
    slots: ["top", "bottom1", "bottom2"],
    columns: "1fr 1fr",
    rows: "repeat(2, 1fr)",
    areas: ["top top", "bottom1 bottom2"],
  },
  "1_3": {
    slots: ["top", "bottom1", "bottom2", "bottom3"],
    columns: "1fr 1fr 1fr",
    rows: "1fr 1fr",
    areas: ["top top top", "bottom1 bottom2 bottom3"],
  },
  "2_1": {
    slots: ["top1", "top2", "bottom"],
    columns: "1fr 1fr",
    rows: "repeat(2, 1fr)",
    areas: ["top1 top2", "bottom bottom"],
  },
  "3_1": {
    slots: ["top1", "top2", "top3", "bottom"],
    columns: "1fr 1fr 1fr",
    rows: "repeat(2, 1fr)",
    areas: ["top1 top2 top3", "bottom bottom bottom"],
  },
  "2-2": {
    slots: ["CPR-0-S", "CS-0-0", "bottom-left", "bottom-right"],
    columns: "1fr 1fr",
    rows: "1fr 1fr",
    areas: ["CPR-0-S CS-0-0", "bottom-left bottom-right"],
  },
  "2-3": {
    slots: ["top-left", "bottom-left", "top", "middle", "bottom"],
    columns: "1fr 1fr",
    rows: "repeat(6, 1fr)",
    areas: [
      "top-left top",
      "top-left top",
      "top-left middle",
      "bottom-left middle",
      "bottom-left bottom",
      "bottom-left bottom",
    ],
  },
  "3-2": {
    slots: ["top", "top-left", "bottom-left", "middle", "bottom"],
    columns: "repeat(8, 1fr)",
    rows: "repeat(4, 1fr)",
    areas: [
      "top top-left top-left top-left middle middle middle middle",
      "top top-left top-left top-left middle middle middle middle",
      "top top-left top-left top-left bottom bottom bottom bottom",
      "bottom-left bottom-left bottom-left bottom-left bottom bottom bottom bottom",
    ],
  },
  "1-1-1": {
    slots: ["left", "middle", "right"],
    columns: "100%",
    rows: "repeat(3, 1fr)",
    areas: ["left", "middle", "right"],
  },
  "1-2-1": {
    slots: ["left", "middle", "bottom", "right"],
  },
  "1-3-2": {
    slots: [
      "CPR-0-S",
      "CS-0-0",
      "CS-0-1",
      "CS-0-2",
      "CPR-0-20",
      "CPR-1-S",
      "CS-1-0",
      "CS-1-1",
      "CS-1-2",
      "CPR-1-20",
      "CPR-2-S",
      "CS-2-0",
      "CS-2-1",
      "CS-2-2",
      "CPR-2-20",
      "Report",
      "Seg",
    ],
    columns: "repeat(16, 1fr)",
    rows: "repeat(8, 1fr)",
    areas: [
      "CPR-0-S CPR-0-S CS-0-0 CS-0-0 CPR-1-S CPR-1-S CS-1-0 CS-1-0 CPR-2-S CPR-2-S CS-2-0 CS-2-0 Report Report Report Report",
      "CPR-0-S CPR-0-S CS-0-0 CS-0-0 CPR-1-S CPR-1-S CS-1-0 CS-1-0 CPR-2-S CPR-2-S CS-2-0 CS-2-0 Report Report Report Report",
      "CPR-0-S CPR-0-S CS-0-1 CS-0-1 CPR-1-S CPR-1-S CS-1-1 CS-1-1 CPR-2-S CPR-2-S CS-2-1 CS-2-1 Report Report Report Report",
      "CPR-0-S CPR-0-S CS-0-1 CS-0-1 CPR-1-S CPR-1-S CS-1-1 CS-1-1 CPR-2-S CPR-2-S CS-2-1 CS-2-1 Report Report Report Report",
      "CPR-0-S CPR-0-S CS-0-2 CS-0-2 CPR-1-S CPR-1-S CS-1-2 CS-1-2 CPR-2-S CPR-2-S CS-2-2 CS-2-2 Seg Seg Seg Seg",
      "CPR-0-S CPR-0-S CS-0-2 CS-0-2 CPR-1-S CPR-1-S CS-1-2 CS-1-2 CPR-2-S CPR-2-S CS-2-2 CS-2-2 Seg Seg Seg Seg",
      "CPR-0-20 CPR-0-20 CPR-0-20 CPR-0-20 CPR-1-20 CPR-1-20 CPR-1-20 CPR-1-20 CPR-2-20 CPR-2-20 CPR-2-20 CPR-2-20 Seg Seg Seg Seg",
      "CPR-0-20 CPR-0-20 CPR-0-20 CPR-0-20 CPR-1-20 CPR-1-20 CPR-1-20 CPR-1-20 CPR-2-20 CPR-2-20 CPR-2-20 CPR-2-20 Seg Seg Seg Seg",
    ],
  },
  "1-2-3": {
    slots: ["report", "seg", "vr", "dicom1", "dicom2", "dicom3"],
  },
  "2-2-2": {
    slots: ["DICOM", "CV-1", "CV-2", "CV-3", "CV-4", "Seg"],
    columns: "repeat(3, 1fr)",
    rows: "repeat(2, 1fr)",
    areas: ["DICOM CV-1 CV-2", "CV-3 CV-4 Seg"],
  },
  "5-2-2": {
    slots: [
      "CPR-0-T",
      "CPR-0-S",
      "CS-0-0",
      "CS-0-1",
      "CS-0-2",
      "CPR-0-20",
      "bottom",
      "right1",
      "right2",
    ],
    columns: "repeat(7, 1fr)",
    rows: "repeat(8, 1fr)",
    areas: [
      "CPR-0-T CPR-0-T CPR-0-S CPR-0-20 CPR-0-20 right1 right1",
      "CPR-0-T CPR-0-T CPR-0-S CPR-0-20 CPR-0-20 right1 right1",
      "CPR-0-T CPR-0-T CPR-0-S CPR-0-20 CPR-0-20 right1 right1",
      "CPR-0-T CPR-0-T CPR-0-S CPR-0-20 CPR-0-20 right1 right1",
      "CPR-0-T CPR-0-T CPR-0-S bottom bottom right2 right2",
      "CPR-0-T CPR-0-T CPR-0-S bottom bottom right2 right2",
      "CS-0-0 CS-0-1 CS-0-2 bottom bottom right2 right2",
      "CS-0-0 CS-0-1 CS-0-2 bottom bottom right2 right2",
    ],
  },
  lesionStackView: {
    slots: [
      "CS-0-0",
      "CS-3-0",
      "CS-3-1",
      "CS-3-2",
      "CS-3-3",
      "CS-3-4",
      "CS-0-2",
      "CPR-0-T",
      "CPR-0-20",
      "bottom",
      "right2",
    ],
    columns: "repeat(7, 1fr)",
    rows: "repeat(14, 1fr)",
    areas: [
      "CPR-0-T CPR-0-T CS-0-0 CPR-0-20 CPR-0-20 CPR-0-20 CPR-0-20",
      "CPR-0-T CPR-0-T CS-0-0 CPR-0-20 CPR-0-20 CPR-0-20 CPR-0-20",
      "CPR-0-T CPR-0-T CS-3-0 CPR-0-20 CPR-0-20 CPR-0-20 CPR-0-20",
      "CPR-0-T CPR-0-T CS-3-0 CPR-0-20 CPR-0-20 CPR-0-20 CPR-0-20",
      "CPR-0-T CPR-0-T CS-3-1 CPR-0-20 CPR-0-20 CPR-0-20 CPR-0-20",
      "CPR-0-T CPR-0-T CS-3-1 CPR-0-20 CPR-0-20 CPR-0-20 CPR-0-20",
      "CPR-0-T CPR-0-T CS-3-2 CPR-0-20 CPR-0-20 CPR-0-20 CPR-0-20",
      "CPR-0-T CPR-0-T CS-3-2 bottom bottom right2 right2",
      "CPR-0-T CPR-0-T CS-3-3 bottom bottom right2 right2",
      "CPR-0-T CPR-0-T CS-3-3 bottom bottom right2 right2",
      "CPR-0-T CPR-0-T CS-3-4 bottom bottom right2 right2",
      "CPR-0-T CPR-0-T CS-3-4 bottom bottom right2 right2",
      "CPR-0-T CPR-0-T CS-0-2 bottom bottom right2 right2",
      "CPR-0-T CPR-0-T CS-0-2 bottom bottom right2 right2",
    ],
  },
  plaqueManuel: {
    slots: [
      "CPR-0-T",
      "CPR-0-S",
      "CS-0-0",
      "CS-0-1",
      "CS-0-2",
      "top",
      "bottom",
    ],
    columns: "repeat(5, 1fr)",
    rows: "repeat(8, 1fr)",
    areas: [
      "CPR-0-T CPR-0-T CPR-0-S top top",
      "CPR-0-T CPR-0-T CPR-0-S top top",
      "CPR-0-T CPR-0-T CPR-0-S top top",
      "CPR-0-T CPR-0-T CPR-0-S top top",
      "CPR-0-T CPR-0-T CPR-0-S bottom bottom",
      "CPR-0-T CPR-0-T CPR-0-S bottom bottom",
      "CS-0-0 CS-0-1 CS-0-2 bottom bottom",
      "CS-0-0 CS-0-1 CS-0-2 bottom bottom",
    ],
  },
  "1-1-1-1": {
    slots: ["left", "middle", "bottom", "right"],
    columns: "100%",
    rows: "repeat(4, 1fr)",
    areas: ["left", "middle", "bottom", "right"],
  },
  "3-3-2-2": {
    slots: [
      "CS-1-0",
      "CS-1-1",
      "CS-1-2",
      "CS-2-0",
      "CS-2-1",
      "CS-2-2",
      "CS-0-0",
      "CPR-0-T",
      "Report",
      "Seg",
    ],
    columns: "repeat(4, 1fr)",
    rows: "repeat(6, 1fr)",
    areas: [
      "CS-1-0 CS-2-0 CS-0-0 Report",
      "CS-1-0 CS-2-0 CS-0-0 Report",
      "CS-1-1 CS-2-1 CS-0-0 Report",
      "CS-1-1 CS-2-1 CPR-0-T Seg",
      "CS-1-2 CS-2-2 CPR-0-T Seg",
      "CS-1-2 CS-2-2 CPR-0-T Seg",
    ],
  },
};

function resolveLayoutSpec(type?: string): LayoutSpec {
  if (type && LAYOUT_REGISTRY[type]) return LAYOUT_REGISTRY[type];
  return {
    slots: [""],
    columns: "repeat(1, minmax(0, 1fr))",
    rows: "repeat(1, minmax(0, 1fr))",
  };
}

interface PanelContainerProps {}
type SegmentationStateForSegmentation = Partial<
  Record<SegmentationControllerType, Partial<SegmentationProps>>
>;

const ZIP_CRC32_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let i = 0; i < 256; i++) {
    let c = i;
    for (let j = 0; j < 8; j++) {
      c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    }
    table[i] = c >>> 0;
  }
  return table;
})();

const crc32 = (bytes: Uint8Array): number => {
  let crc = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) {
    crc = ZIP_CRC32_TABLE[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
};

const concatUint8Arrays = (chunks: Uint8Array[]): Uint8Array => {
  const total = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  chunks.forEach((chunk) => {
    out.set(chunk, offset);
    offset += chunk.length;
  });
  return out;
};

const createStoredZip = (
  files: { name: string; data: Uint8Array }[],
): Uint8Array => {
  const encoder = new TextEncoder();
  const localParts: Uint8Array[] = [];
  const centralParts: Uint8Array[] = [];
  let offset = 0;

  files.forEach((file) => {
    const nameBytes = encoder.encode(file.name);
    const data = file.data;
    const crc = crc32(data);
    const localOffset = offset;

    const localHeader = new Uint8Array(30);
    const localView = new DataView(localHeader.buffer);
    localView.setUint32(0, 0x04034b50, true);
    localView.setUint16(4, 20, true);
    localView.setUint16(6, 0, true);
    localView.setUint16(8, 0, true);
    localView.setUint16(10, 0, true);
    localView.setUint16(12, 0, true);
    localView.setUint32(14, crc, true);
    localView.setUint32(18, data.length, true);
    localView.setUint32(22, data.length, true);
    localView.setUint16(26, nameBytes.length, true);
    localView.setUint16(28, 0, true);

    localParts.push(localHeader, nameBytes, data);
    offset += localHeader.length + nameBytes.length + data.length;

    const centralHeader = new Uint8Array(46);
    const centralView = new DataView(centralHeader.buffer);
    centralView.setUint32(0, 0x02014b50, true);
    centralView.setUint16(4, 20, true);
    centralView.setUint16(6, 20, true);
    centralView.setUint16(8, 0, true);
    centralView.setUint16(10, 0, true);
    centralView.setUint16(12, 0, true);
    centralView.setUint16(14, 0, true);
    centralView.setUint32(16, crc, true);
    centralView.setUint32(20, data.length, true);
    centralView.setUint32(24, data.length, true);
    centralView.setUint16(28, nameBytes.length, true);
    centralView.setUint16(30, 0, true);
    centralView.setUint16(32, 0, true);
    centralView.setUint16(34, 0, true);
    centralView.setUint16(36, 0, true);
    centralView.setUint32(38, 0, true);
    centralView.setUint32(42, localOffset, true);

    centralParts.push(centralHeader, nameBytes);
  });

  const localBlob = concatUint8Arrays(localParts);
  const centralBlob = concatUint8Arrays(centralParts);
  const eocd = new Uint8Array(22);
  const eocdView = new DataView(eocd.buffer);
  eocdView.setUint32(0, 0x06054b50, true);
  eocdView.setUint16(4, 0, true);
  eocdView.setUint16(6, 0, true);
  eocdView.setUint16(8, files.length, true);
  eocdView.setUint16(10, files.length, true);
  eocdView.setUint32(12, centralBlob.length, true);
  eocdView.setUint32(16, localBlob.length, true);
  eocdView.setUint16(20, 0, true);

  return concatUint8Arrays([localBlob, centralBlob, eocd]);
};

const PanelContainer: React.FC<PanelContainerProps> = ({}) => {
  const TAVI_MAX_RETRY_COUNT = 3;
  const TAVI_MAX_ATTEMPTS = TAVI_MAX_RETRY_COUNT + 1;
  const divRef = useRef<HTMLDivElement | null>(null);
  const analysisViewLoadingModalIdRef = useRef<string | null>(null);
  const coronarydowncheck = useRef<boolean>(false);
  const aortadowncheck = useRef<boolean>(false);
  const taviRequestAttemptsRef = useRef<Record<string, number>>({});
  const retryTransitionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const prevRef = useRef<{ seriesId?: string; analysisType?: AnalysisType }>(
    {},
  );
  const [isRetryingTavi, setIsRetryingTavi] = React.useState(false);
  const taviViewRebuildAttemptsRef = useRef<number>(0);

  const {
    selectedStudySeries,
    selectedSeries,
    selectedSeriesId,
    topLeftInfo,
    topRightInfo,
    selectedSeriesImageTag,
    seriesSelections,
    updateSeriesSections,
  } = useBrowserPage();
  const { currentVOI } = useAnalysisPage();

  const { accessToken } = useAuth();
  const { openModal, closeModal } = useModal();

  const {
    isLayoutStateUpdateData,
    isLayoutStateLoading,
    selectedLayoutConfig,
    selectedAnalysisDefault,
    switchConfigs,
    globalToolState,
    handleLayoutStateLoading,
    updateCurrentLayout,
    updateWindowLevelWidth,
  } = useAnalysisPage();
  const { handleToolActivate } = useTool();

  const {
    analysisType,
    fullscreenPanelComponentKey,
    isCornerstoneInitialized,
    panelStates,
    updateSelectedPanelConfig,
    updatePanelState,
    updateCPRState,
    updateFullscreenPanelComponentKey,
  } = usePanelContainer();
  const AutoCaptureModalOpen = useAutoCaptureModal();

  const { config, setData } = useConfig();
  const { bufferPool, contextPool, setBufferPool } = useBufferPool();
  const pool = selectedSeriesId ? bufferPool[selectedSeriesId] : undefined;
  const hasDicomPanelInLayout = useMemo(
    () =>
      selectedLayoutConfig.panelConfig.some((panel) =>
        ["Axial", "Coronal", "Sagittal"].includes(panel.componentKey),
      ),
    [selectedLayoutConfig.panelConfig],
  );
  const syncDicomZoomForFitRef = React.useRef(
    globalToolState?.toggles.ToggleDicomZoomSync ?? true,
  );

  useEffect(() => {
    syncDicomZoomForFitRef.current =
      globalToolState?.toggles.ToggleDicomZoomSync ?? true;
  }, [globalToolState?.toggles.ToggleDicomZoomSync]);
  const [ffrThumbnails, setFfrThumbnails] = React.useState<CaptureThumb[]>([]);
  const [ffrCaptureRequest, setFfrCaptureRequest] = React.useState<{
    nonce: number;
    reset: boolean;
  } | null>(null);
  const ffrThumbIdRef = useRef(1);
  const ffrCaptureInFlightRef = useRef(false);

  const scheduleDicomFitToScreen = useCallback(() => {
    if (!selectedSeriesId || !hasDicomPanelInLayout) {
      resizeEngine();
      return;
    }
    if (
      fullscreenPanelComponentKey &&
      fullscreenPanelComponentKey !== "Empty" &&
      !["Axial", "Coronal", "Sagittal"].includes(fullscreenPanelComponentKey)
    ) {
      resizeEngine();
      return;
    }

    fitDicomViewportsToScreen({
      syncToAxial: syncDicomZoomForFitRef.current,
      repeat: true,
    });
  }, [fullscreenPanelComponentKey, hasDicomPanelInLayout, selectedSeriesId]);

  useEffect(() => {
    if (!selectedSeriesId) return;
    if (!pool) {
      setupOwnPool(setBufferPool, selectedSeriesId);
    }
  }, [selectedSeriesId, pool]);

  // console.log(
  //     prevRef.current.seriesId,
  //     selectedSeriesId,
  //     segmentationStateRef.current,
  //     panelStates.Surface3D.segmentation?.coronary.visibility
  //   );

  useEffect(() => {
    console.log(selectedSeriesId, prevRef.current.seriesId);
    if (!selectedSeriesId) return;
    if (prevRef.current.seriesId === selectedSeriesId) return;
    taviRequestAttemptsRef.current = {};
    if (retryTransitionTimerRef.current) {
      clearTimeout(retryTransitionTimerRef.current);
      retryTransitionTimerRef.current = null;
    }
    setIsRetryingTavi(false);
    setFfrCaptureRequest(null);
    setFfrThumbnails([]);
    ffrThumbIdRef.current = 1;
    const rrPercent = calculateRRPercent(selectedSeries);
    setData({ rrPercent });
    prevRef.current = { seriesId: selectedSeriesId, analysisType: undefined };
  }, [selectedSeriesId]);

  useEffect(() => {
    //ef 초기 셋팅용
    if (analysisType !== "ejectionFraction") return;
    if (seriesSelections.length !== 0) return;
    const parameterData = pool?.parameter?.data;
    const efInfo = (
      parameterData as
        | {
            SavedInfo?: {
              EjectionFraction?: {
                Info?: Record<string, { Series?: string }>;
              };
            };
          }
        | undefined
    )?.SavedInfo?.EjectionFraction?.Info;
    if (!efInfo) return;

    const select = Object.entries(efInfo)
      .map(([percent, value]) => {
        const id = (value as { Series?: string })?.Series;
        return id ? { id, percent } : null;
      })
      .filter((item): item is { id: string; percent: string } => item !== null);
    if (select.length > 0) {
      updateSeriesSections(select);
    }
  }, [analysisType, seriesSelections]);

  useEffect(() => {
    return () => {
      if (retryTransitionTimerRef.current) {
        clearTimeout(retryTransitionTimerRef.current);
        retryTransitionTimerRef.current = null;
      }
    };
  }, []);

  const { branchIds, mainBranchIds, setBranchIds, setMainBranchId } =
    useBranchInfo();

  const projectionList = getProjectionList(analysisType);

  useEffect(() => {
    if (fullscreenPanelComponentKey) {
      updateFullscreenPanelComponentKey(undefined);
    }
  }, [analysisType, selectedLayoutConfig.type]);

  // const layoutClassName = selectedLayoutConfig.type
  //   ? styles[`container-${selectedLayoutConfig.type}`]
  //   : styles['container'];

  const layoutSpec = useMemo(
    () => resolveLayoutSpec(selectedLayoutConfig.type),
    [selectedLayoutConfig.type],
  );
  const panelClassName: string[] = useMemo(
    () => layoutSpec.slots,
    [layoutSpec],
  );
  const containerGridStyle = useMemo(() => {
    const style: React.CSSProperties = {};
    if (layoutSpec.columns) style.gridTemplateColumns = layoutSpec.columns;
    if (layoutSpec.rows) style.gridTemplateRows = layoutSpec.rows;
    if (layoutSpec.areas && layoutSpec.areas.length > 0) {
      style.gridTemplateAreas = layoutSpec.areas
        .map((row) => `"${row}"`)
        .join(" ");
    }
    return style;
  }, [layoutSpec]);

  const [downloaded, setDownloaded] = React.useState(0);
  const [segVolAdded, setSegVolAdded] = React.useState(true);
  const downloadBatchIdRef = useRef(0);
  // const [DICOMReady, setDICOMReady] = React.useState(false);
  // const refSegVolAdded = React.useRef(true);
  const { segmentation } = panelStates["Surface3D"];

  // TODO: segmentation init flow can be simplified later.
  // useEffect(() => {
  //   const panelState = panelStates['Surface3D'];
  //   const { segmentation } = panelState;

  //   if (!segmentation) return;
  //   const seg = getRequestSegmentation(analysisType, segmentation);
  //   (Object.keys(seg) as (keyof typeof seg)[]).forEach((key) => {
  //     let vi = true;
  //     let op = 1;
  //     if (key === 'epicardial') {
  //       vi = segmentation[key].visibility;
  //       op = segmentation[key].opacity;
  //     }
  //     updatePanelState(key, 'segmentation', {
  //       ...segmentation,
  //       [key]: { ['visibility']: vi, ['opacity']: op },
  //     });
  //   });
  //   // Object.entries(segmentation).map(([key, val]) => {
  //   //   const name = key as SegmentationControllerType;
  //   //   updatePanelState(name, 'segmentation', {
  //   //     ...segmentation,
  //   //     [name]: { ['visibility']: true, ['opacity']: 1 },
  //   //   });
  //   // });
  //   config.heartCut = false;
  // }, [analysisType]);
  const { renderingQuality } = config.generalSetting;
  const voidAnalysisType = ["preview", "mpr"] as AnalysisType[];
  const segmentationAnalysisType = [
    "chamber",
    "aorticValveCalcium",
    "tavi",
    "calciumScore",
    "fai",
    "isr",
    "myocardial",
    "plaque",
    "segmentation",
    "stenosis",
    "wallthickness",
    "FFR",
  ] as AnalysisType[];
  const branchesAnalysisType = [
    //'calciumScore',
    //'fai',
    //'isr',
    //'plaque',
    //'stenosis',
    //'myocardial',
  ] as AnalysisType[];
  const centerLineAnalysisType = [
    "calciumScore",
    "fai",
    "isr",
    "plaque",
    "stenosis",
    "myocardial",
    "aorticValveCalcium",
    "tavi",
  ] as AnalysisType[];

  const segmentationHalfAnalysisType = [] as AnalysisType[];
  const wallAnalysisType = ["wallthickness"] as AnalysisType[];

  const makeDesc = (batchId: number) => () =>
    setDownloaded((v) =>
      batchId === downloadBatchIdRef.current ? Math.max(v - 1, 0) : v,
    );

  const lowQuality = renderingQuality !== "high";
  const cprCrossSectionImageMode = getConfiguredCprCrossSectionImageMode();
  const cprCrossSectionImageManifestTarget =
    getConfiguredCprCrossSectionImageManifestTarget();
  const forceLegacyCprForFfrLumenEdit = shouldForceLegacyCprForFfrLumenEdit(
    analysisType,
    config.EditMode,
  );
  const configuredRequiresCprCrossSectionImageShell =
    !forceLegacyCprForFfrLumenEdit &&
    shouldUseCprCrossSectionImageShellForLayout(
      cprCrossSectionImageMode,
      selectedLayoutConfig.panelConfig,
    );
  const cprCrossSectionImageManifestStatus =
    configuredRequiresCprCrossSectionImageShell
      ? getCprCrossSectionImageManifestStatus(cprCrossSectionImageMode, pool)
      : "disabled";
  const effectiveCprCrossSectionImageMode =
    getEffectiveCprCrossSectionImageMode(
      cprCrossSectionImageMode,
      pool,
      configuredRequiresCprCrossSectionImageShell,
    );
  const requiresCprCrossSectionImageShell =
    shouldUseCprCrossSectionImageShellForLayout(
      effectiveCprCrossSectionImageMode,
      selectedLayoutConfig.panelConfig,
    );
  const cprCrossSectionImageShellReady =
    !requiresCprCrossSectionImageShell ||
    isCprCrossSectionImageShellReady(effectiveCprCrossSectionImageMode, pool);
  function shootUpdateLabelmap(seriesId: string, vr: boolean) {
    // requestAnimationFrame(() => {
    scheduleRender("Axial", { type: "updateLabelmap", seriesId });
    scheduleRender("Coronal", { type: "updateLabelmap", seriesId });
    scheduleRender("Sagittal", { type: "updateLabelmap", seriesId });
    // if(!vr)
    //   scheduleRender('VR', { type: 'updateLabelmap' });
    // });
  }
  useEffect(() => {
    if (!selectedSeriesId) return;
    if (downloaded === 0) {
      const f = async () => {
        let res = false;
        if (segmentationAnalysisType.some((v) => v == analysisType))
          res ||= await checkLabelmap(selectedSeriesId!, lowQuality);
        else if (segmentationHalfAnalysisType.some((v) => v == analysisType)) {
          selectedStudySeries?.forEach(async (series) => {
            res ||= await checkLabelmap(series.id, true);
          });
        }
        return res;
      };
      f().then((res) => {
        // if(res){
        shootUpdateLabelmap(selectedSeriesId, res);
        // }
      });

      // if(!!passed.length){

      // }
    }
  }, [downloaded]);

  // // Scroll lock sample.
  //   const originalStyle = window.getComputedStyle(document.body).overflow;
  //   // document.body.style.overflow = 'hidden';
  //   return () => {
  //     document.body.style.overflow = originalStyle;
  //   };
  // },[])

  // [AnalysisType]

  useEffect(() => {
    if (!selectedSeriesId) return;
    if (!analysisType) return;
    // if(!pool) return;

    let res = 0;
    let segPass = false;
    downloadBatchIdRef.current += 1;
    const batchId = downloadBatchIdRef.current;
    const desc = makeDesc(batchId);

    const parameter = pool?.parameter?.data;
    if (renderingQuality !== "high" && analysisType === "preview") {
      processOriginalData({
        setBufferPool,
        seriesId: selectedSeriesId,
        accessToken,
      });
      // }
      // res += downloadQueueByType({
      //   seriesId: selectedSeriesId,
      //   accessToken,
      //   type: 'original',
      //   desc,
      //   tags: selectedSeriesImageTag,
      // });
    }
    if (segmentationAnalysisType.some((v) => v == analysisType)) {
      const count = downloadQueueByType({
        seriesId: selectedSeriesId,
        accessToken,
        type: lowQuality ? "segmentationHalf" : "segmentation",
        desc,
        tags: selectedSeriesImageTag,
        parameter,
      });
      if (!!count) {
        res += count;
        setSegVolAdded(false);
        // refSegVolAdded.current=false;
        segPass = true;
      }
      // setSegVolAdded(false);
    }
    if (branchesAnalysisType.some((v) => v == analysisType)) {
      const count = downloadQueueByType({
        seriesId: selectedSeriesId,
        accessToken,
        type: "branches",
        parameter,
        desc,
      });
      if (!!count) {
        res += count;
      }
    }
    if (wallAnalysisType.some((v) => v == analysisType)) {
      res += downloadQueueByType({
        seriesId: selectedSeriesId,
        accessToken,
        type: lowQuality ? "wallthicknessHalf" : "wallthickness",
        desc,
        tags: selectedSeriesImageTag,
        parameter,
      });
    }
    if (segmentationHalfAnalysisType.some((v) => v == analysisType)) {
      selectedStudySeries?.forEach((series) => {
        res += downloadQueueByType({
          seriesId: series.id,
          accessToken,
          type: "segmentationHalf",
          desc,
          tags: series.imageTag,
          parameter,
        });
      });
    }
    setDownloaded(res);
    const volumeCacheVolumeAdded = (evt: Event) =>
      volumeCacheVolumeAddedEvent({
        evt,
        callback: () => {
          setSegVolAdded(true);
          handleLayoutStateLoading(false);
          shootUpdateLabelmap(selectedSeriesId, false);
        },
        callbackTag: "NONO",
        forceCallCallback: true,
      });
    if (segPass) addCornerstoneCoreEvents({ volumeCacheVolumeAdded });

    // For non-seg flow, stop layout loading immediately.
    if (isLayoutStateLoading && analysisType !== "preview" && !segPass) {
      handleLayoutStateLoading(false);
    }

    const defaultPanelConfig =
      selectedLayoutConfig.panelConfig.find(
        (p) => p.panelId === "DICOMPanel",
      ) ?? selectedLayoutConfig.panelConfig[0];
    if (defaultPanelConfig) {
      updateSelectedPanelConfig(defaultPanelConfig);
    }

    if (segmentation) {
      const segmentationLabel = config.segmentationLabel;
      const prevAnalysisType = prevRef.current.analysisType;

      // segmentation 상태 저장/복원 유지
      // - analysisType이 segmentation 이탈 시 현재 상태 저장
      // - 동일 시리즈 재진입 시 현재 상태 저장 후 기존 상태 유지
      const shouldSyncSegmentationState =
        (prevAnalysisType === "segmentation" &&
          analysisType !== "segmentation") ||
        (prevAnalysisType === undefined && analysisType === "segmentation");
      // segmentation 상태 저장
      if (shouldSyncSegmentationState) {
        for (const key of Object.keys(
          segmentation,
        ) as SegmentationControllerType[]) {
          const { opacity, visibility } = segmentation[key];
          segmentationLabel.set(key, {
            opacity,
            visibility,
          });
        }
        setData({ segmentationLabel });
      }
      const seg = getSegmentationAnalysisType(analysisType);
      let target: "ventricle" | "coronaryAorta" | undefined;
      if (analysisType === "plaque") {
        target = "coronaryAorta";
      } else if (analysisType === "segmentation") {
        target = config.ventricle
          ? "ventricle"
          : config.coronaryAorta
            ? "coronaryAorta"
            : undefined;
      }

      const offSegSet = new Set(target ? getOffSegmentation(target) : []);
      for (const key of seg) {
        const savedSegmentation =
          analysisType === "segmentation"
            ? segmentationLabel.get(key)
            : undefined;
        updatePanelState(key, "segmentation", {
          ...segmentation,
          [key]: {
            ...segmentation[key],
            visibility: savedSegmentation?.visibility ?? !offSegSet.has(key),
            opacity: 1,
          },
        });
      }
      prevRef.current = { seriesId: selectedSeriesId, analysisType };
    }

    if (!selectedSeriesImageTag) return;
    // Apply default window/level if needed.
    // const wl = selectedSeriesImageTag.windowCenter ?? 600;
    // const ww = selectedSeriesImageTag.windowWidth ?? 600;
    // console.log('VOI apply', currentVOI);
    // if(selectedAnalysisDefault){
    //   updateWindowLevelWidth([selectedAnalysisDefault.windowLevel,selectedAnalysisDefault.windowWidth]);
    // }

    return () => {
      if (segPass) {
        removeCornerstoneCoreEvents({ volumeCacheVolumeAdded });
      }
    };
  }, [analysisType, isLayoutStateUpdateData]);

  // useEffect(() => {
  //   if (!selectedSeriesId)return;
  //   if(!analysisType) return;

  //   return ()=> {
  //     if(analysisType !== 'empty' && config[analysisType]){
  //       //console.log('currentVOI',currentVOI,analysisType)
  //       config[analysisType].windowLevelParams = {
  //         ...config[analysisType].windowLevelParams,
  //         currentWindowLevel:currentVOI.windowLevel,
  //         currentWindowWidth:currentVOI.windowWidth,
  //       };
  //     }
  //   }
  // }, [analysisType,currentVOI]);

  if (!!downloaded) {
    console.log(analysisType, "pending downloads", downloaded);
  }

  useEffect(() => {
    if (!selectedSeriesId) return;
    if (!pool) return;
    if (
      !shouldRequestCprCrossSectionImages(
        cprCrossSectionImageMode,
        pool,
        configuredRequiresCprCrossSectionImageShell,
        analysisType,
      )
    ) {
      return;
    }

    requestCprCrossSectionImages(
      pool,
      setBufferPool,
      accessToken,
      selectedSeriesId,
      cprCrossSectionImageMode,
      config,
      analysisType,
      undefined,
      cprCrossSectionImageManifestTarget,
      currentVOI,
      mainBranchIds,
    );
  }, [
    accessToken,
    analysisType,
    config,
    cprCrossSectionImageManifestTarget,
    cprCrossSectionImageMode,
    currentVOI,
    mainBranchIds,
    pool,
    configuredRequiresCprCrossSectionImageShell,
    selectedSeriesId,
    setBufferPool,
  ]);

  useEffect(() => {
    if (!selectedSeriesId) return;
    if (!bufferPool) return;
    if (!pool) return;
    if (!analysisType) return;
    if (!!downloaded || !segVolAdded) return;
    if (isLayoutStateLoading) return;
    if (analysisType !== "ejectionFraction") {
      getSegmentationBufferKeys(analysisType).forEach((key) => {
        if (!pool[key].loaded) {
          requestDraco(
            pool,
            setBufferPool,
            accessToken,
            selectedSeriesId,
            key,
            renderingQuality!,
          );
        }
      });
    }

    if (analysisType === "wallthickness") {
      if (!pool["LAWT"].loaded) {
        requestMappingData(
          pool,
          setBufferPool,
          accessToken,
          selectedSeriesId,
          "LAWT",
        );
      }
    }

    //if (!pool['mapping_branches'].loaded) {
    //  const hasCoronary =
    //    getSegmentationAnalysisType(analysisType).includes('coronary');
    //  if (hasCoronary) {
    //    requestBranches(
    //      pool,
    //      setBufferPool,
    //      accessToken,
    //      selectedSeriesId,
    //      'mapping_branches',
    //    );
    //  }
    //}

    if (analysisType === "FFR") {
      if (!pool["ct_ffr"].loaded) {
        requestBufferPoolJson(
          pool,
          setBufferPool,
          accessToken,
          selectedSeriesId,
          "ct_ffr",
        );
      } else {
        Object.values(FFRNames).forEach((key) => {
          if (!pool[key].loaded) {
            requestPolyDataModel(
              pool,
              setBufferPool,
              accessToken,
              selectedSeriesId,
              key,
              renderingQuality!,
            );
          }
        });
      }
    }

    if (analysisType === "myocardial") {
      if (!pool["territory"].loaded) {
        requestTerrData(pool, setBufferPool, accessToken, selectedSeriesId);
      }
      if (!pool["startNode_list"].loaded) {
        requestCPRData(
          pool,
          setBufferPool,
          accessToken,
          selectedSeriesId,
          "startNode_list",
        );
      }
    }

    const shouldPrepareCprCenterlineData =
      centerLineAnalysisType.some((v) => v == analysisType) ||
      config.EditMode === EditNames.lumen;

    if (shouldPrepareCprCenterlineData) {
      const aorticCenterlineId =
        analysisType === "tavi"
          ? (config.tavi_aorta_id ?? config.aorta_id)
          : config.aorta_id;
      const hasAortaCenterline = !!(
        pool.centerData.data as Record<number, vtkPolyData> | undefined
      )?.[aorticCenterlineId];
      const hasTaviResourcesReady =
        analysisType !== "tavi"
          ? true
          : pool.parameter.loaded &&
            TAVI_RESOURCE_KEYS.every(
              (k) =>
                !!(pool as any)?.[k]?.loaded ||
                !!(pool as any)?.[k]?.retryExhausted,
            );
      if (
        pool.branchGraphDataset.loaded &&
        pool.centerData.loaded &&
        (analysisType === "aorticValveCalcium" || analysisType === "tavi"
          ? hasAortaCenterline
          : true) &&
        pool.labelData.loaded &&
        hasTaviResourcesReady
      ) {
        return;
      }
      Object.entries(pool).forEach(async ([key, value]) => {
        switch (key) {
          case "branchGraphDataset":
            if (!value.loaded) {
              arrangeBranchDatasetWorker(
                pool,
                setBufferPool,
                selectedSeriesId,
                selectedSeriesImageTag?.pixelSpacing!,
                selectedSeriesImageTag?.planarSpacingInterpolated!,
              );
            }
            break;
          case "branchLabel":
            if (value.loaded) {
              arrangeLabelDataWorker(pool, setBufferPool, selectedSeriesId, [
                selectedSeriesImageTag?.width!,
                selectedSeriesImageTag?.height!,
                selectedSeriesImageTag?.depthInterpolated!,
              ]);
            } else {
              requestCPRData(
                pool,
                setBufferPool,
                accessToken,
                selectedSeriesId,
                key,
              );
            }
            break;
          case "parameter":
            if (!value.loaded) {
              requestBufferPoolJson(
                pool,
                setBufferPool,
                accessToken,
                selectedSeriesId,
                key,
              );
            }
            break;
          case "plaque_info_path":
          case "new_area_path":
          case "new_diameter":
          case "new_wall_path":
          case "new_wall_area_path":
          case "centerline":
          case "fcx":
          case "fcy":
          case "fcz":

          case "fxx":
          case "fxy":
          case "fxz":
          case "fyx":
          case "fyy":
          case "fyz":

          case "newType":
          case "branchid":
          case "type_total":
            if (value.loaded) {
              splitBaseCenterDataWorker(
                pool,
                setBufferPool,
                selectedSeriesId,
                config,
                setData,
                coronarydowncheck,
              );
            } else {
              requestCPRData(
                pool,
                setBufferPool,
                accessToken,
                selectedSeriesId,
                key,
              );
            }
            break;
          case "aorta_centerline":
            if (value.loaded) {
              splitAortaValveCalciumDataWorker(
                pool,
                setBufferPool,
                selectedSeriesId,
                config,
                setData,
                aortadowncheck,
              );
            } else {
              requestCPRData(
                pool,
                setBufferPool,
                accessToken,
                selectedSeriesId,
                key,
              );
            }
            break;
          case "tavi.centerline":
          case "tavi.cusp":
          case "tavi.ostium":
          case "tavi.sinotubularjunction":
          case "tavi.sinus":
          case "tavi.annulus":
          case "tavi.center_dx":
          case "tavi.center_dy":
          case "tavi.centerTangent":
          case "tavi.measurements":
          case "tavi.heightPlane": {
            if (analysisType !== "tavi") break;
            if (!pool.parameter.loaded) break;
            const loadingItem = value as any;
            const retryExhausted = !!loadingItem?.retryExhausted;
            if (value.loaded || retryExhausted) {
              if (selectedSeriesId && value.loaded) {
                delete taviRequestAttemptsRef.current[
                  `${selectedSeriesId}:${key}`
                ];
              }
              splitTaviDataWorker(
                pool,
                setBufferPool,
                selectedSeriesId,
                config,
                setData,
                aortadowncheck,
              );
            } else {
              const requestedAt =
                typeof loadingItem?.requestedAt === "number"
                  ? loadingItem.requestedAt
                  : 0;
              const isStuckLoading =
                !!loadingItem?.loading &&
                requestedAt > 0 &&
                Date.now() - requestedAt > 30000;
              const isLoadingWithoutTimestamp =
                !!loadingItem?.loading && requestedAt <= 0;
              const canRetryNow =
                !loadingItem?.loading ||
                !!loadingItem?.error ||
                isStuckLoading ||
                isLoadingWithoutTimestamp;
              if (!canRetryNow) break;

              const retryKey = `${selectedSeriesId}:${key}`;
              const currentAttempts =
                taviRequestAttemptsRef.current[retryKey] ?? 0;

              if (currentAttempts >= TAVI_MAX_ATTEMPTS) {
                if (!retryExhausted) {
                  setBufferPool((prev) => {
                    const currentSeriesPool = prev[selectedSeriesId];
                    const currentItem = currentSeriesPool?.[key] as any;
                    if (!currentSeriesPool || !currentItem) return prev;
                    return {
                      ...prev,
                      [selectedSeriesId]: {
                        ...currentSeriesPool,
                        [key]: {
                          ...currentItem,
                          loading: false,
                          loaded: false,
                          error: true,
                          requestedAt: undefined,
                          retryExhausted: true,
                        },
                      },
                    };
                  });
                }
                break;
              }

              taviRequestAttemptsRef.current[retryKey] = currentAttempts + 1;
              requestCPRData(
                pool,
                setBufferPool,
                accessToken,
                selectedSeriesId,
                key,
                isStuckLoading || !!loadingItem?.error,
              );
            }
            break;
          }
          default:
            break;
        }
      });
    }

    // if(!globalToolState) return;
    // if((globalToolState.toggles.ToggleSync && analysisType==='preview') ||
    //   !globalToolState.toggles.ToggleSync && analysisType!=='preview'){
    //   handleToolActivate('ToggleSync');
    // }
  }, [
    analysisType,
    accessToken,
    selectedSeriesId,
    bufferPool,
    pool,
    downloaded,
    segVolAdded,
    isLayoutStateLoading,
    config.EditMode,
    cprCrossSectionImageManifestTarget,
    cprCrossSectionImageMode,
  ]);

  useEffect(() => {
    if (!selectedSeriesId) return;
    if (!pool) return;
    const typeTotalData = pool?.type_total?.data;
    const branchGraphData = pool?.branchGraphDataset?.data;
    if (!typeTotalData) return;
    if (!branchGraphData) return;

    if (pool.parameter.loaded && mainBranchIds.every((v) => v === 0)) {
      const newBranchId = ensureMainBranchIds(branchGraphData, typeTotalData);

      if (newBranchId) {
        setMainBranchId(newBranchId);
        updateCPRState({ mainIndex: { index: 0, branchId: newBranchId[0] } });
        updateCPRState({ mainIndex: { index: 1, branchId: newBranchId[1] } });
        updateCPRState({ mainIndex: { index: 2, branchId: newBranchId[2] } });
        const newBranchNames = ["RCA", "LAD", "LCX"]; //

        const newBranchIds = branchIds.map((obj) => {
          const branchId = Number(Object.keys(obj)[0]);
          const idx = newBranchId.indexOf(branchId);
          if (idx !== -1) {
            return { [branchId]: newBranchNames[idx] };
          }
          return obj;
        });
        setBranchIds(newBranchIds);
      }
    }
  }, [pool?.branchGraphDataset?.data, pool?.type_total?.data]);

  // Initialize panel state from parameter.json
  useEffect(() => {
    if (!selectedSeriesId) return;
    if (!pool) return;
    // if(!branchIds.length)
    const parameter = pool.parameter.data;
    if (parameter) {
      const res = handleParameter(parameter);
      console.log("pool.parameter:", parameter);
      setBufferPool((prev) => ({
        ...prev,
        [selectedSeriesId]: applyParameterPaths(pool, pool.parameter.data),
      }));

      // const rrPercent = (parameter as any).imageTag.phase?? 0;
      // setData({ rrPercent });

      if (res) {
        setBranchIds(res.branchIds);
        setMainBranchId(res.mainBranchIds);

        config.avcCalciumScore = Number(
          (res.result.SavedInfo?.Aorta?.AVC_score || 0).toFixed(1),
        );
        config.avcCalciumVolume = Number(
          (res.result.SavedInfo?.Aorta?.AVC_volume || 0).toFixed(1),
        );

        config.agatstonScore = (
          res.result.SavedInfo?.Coronary?.AgatstonScore || Array(10).fill(0)
        ).map((v) => Number((Number(v) || 0).toFixed(1)));
        let indexedStates: IndexedStates = Object.fromEntries(
          res.branchIds.map((obj) => [
            Number(Object.keys(obj)[0]),
            [
              { index: 0 + config.coronary_front_expand },
              { index: 50 + config.coronary_front_expand },
              { index: 100 + config.coronary_front_expand },
            ],
          ]),
        );
        indexedStates[config.aorta_id] = [{ index: -1 }];
        indexedStates[config.tavi_aorta_id ?? config.aorta_id] = [
          { index: -1 },
        ];
        updateCPRState({ mainIndices: res.mainBranchIds, indexedStates });
      }
    } else {
      requestBufferPoolJson(
        pool,
        setBufferPool,
        accessToken,
        selectedSeriesId,
        "parameter",
      );
    }
    //config.heartCut = false;
    //config.ventricle = false;
    //config.coronaryAorta = false;
  }, [accessToken, pool?.parameter.loaded]);

  useEffect(() => {
    scheduleDicomFitToScreen();
  }, [
    analysisType,
    config.EditMode,
    scheduleDicomFitToScreen,
    selectedLayoutConfig.type,
    selectedSeriesId,
  ]);

  // For screenshot capture.
  renderers["panel"] = {
    async render(args?: any) {
      const { type } = args;
      switch (type) {
        case "fullscreencapture":
          // waitingAction({ message: 'Capturing' });
          let url = await screenshotElement(divRef.current!);
          let buffers: BufferData[] = [];
          if (url) {
            const buffer = await imageUrlToArrayBufferSimple(url);
            buffers.push(buffer);
          }

          const data = {
            buffers: buffers,
            screen_capture: true,
            capture_kind: "Report",
            series_description: "fullscreenCapture",
          };
          // closeLoadingModal();
          AutoCaptureModalOpen("fullscreenCapture", [data]);
          break;
        case "ffrCaptureRequest":
          setFfrCaptureRequest({
            nonce: Date.now(),
            reset: !!args?.reset,
          });
          break;

        // case 'checkDICOMPanelsEnabled':
        //   // setDICOMReady(
        //   //   getViewportIdsEnabled(
        //   //     selectedLayoutConfig.panelConfig
        //   //     .filter(p=>p.panelId==='DICOMPanel')
        //   //     .map(p=>p.componentKey)))
        // break;
      }
    },
  };

  const topLeftContent = useMemo(
    () =>
      topLeftInfo.map((info, index) => (
        <div key={index} className="plain-text">
          {info}
        </div>
      )),
    [topLeftInfo],
  );
  const topRightContent = useMemo(
    () => (
      <>
        {(!globalToolState?.toggles.TogglePatientData ? [] : topRightInfo)?.map(
          (info, index) => (
            <div key={index} className="plain-text">
              {info}
            </div>
          ),
        )}
      </>
    ),
    [globalToolState?.toggles.TogglePatientData, topRightInfo],
  );

  const taviFailedKeys = useMemo(() => {
    if (analysisType !== "tavi" || !pool) return [];
    return TAVI_RESOURCE_KEYS.filter(
      (k) => !!(pool as any)?.[k]?.retryExhausted,
    );
  }, [analysisType, pool]);

  const hasTaviFatalFailure =
    analysisType === "tavi" && taviFailedKeys.length > 0;
  const showTaviFailureScreen = hasTaviFatalFailure || isRetryingTavi;
  const hasSeriesSelection = !!selectedSeriesId && !!selectedSeries;

  const centerlineRenderReady = useMemo(() => {
    if (!analysisType) return false;
    if (!centerLineAnalysisType.some((v) => v === analysisType)) return true;
    if (!pool) return false;

    const aorticCenterlineId =
      analysisType === "tavi"
        ? (config.tavi_aorta_id ?? config.aorta_id)
        : config.aorta_id;
    const hasAortaCenterline = !!(
      pool.centerData.data as Record<number, vtkPolyData> | undefined
    )?.[aorticCenterlineId];
    const hasTaviResourcesReady =
      analysisType !== "tavi"
        ? true
        : pool.parameter.loaded &&
          TAVI_RESOURCE_KEYS.every(
            (k) =>
              !!(pool as any)?.[k]?.loaded ||
              !!(pool as any)?.[k]?.retryExhausted,
          );
    const hasTaviParsedReady =
      analysisType !== "tavi" ? true : !!(pool as any)?.["tavi.parsed"]?.loaded;

    // Keep TAVI gate strict so loading UI does not disappear before view render prerequisites.
    if (analysisType === "tavi") {
      return (
        pool.branchGraphDataset.loaded &&
        pool.labelData.loaded &&
        pool.centerData.loaded &&
        hasTaviResourcesReady &&
        hasTaviParsedReady &&
        hasAortaCenterline
      );
    }

    return (
      pool.branchGraphDataset.loaded &&
      pool.centerData.loaded &&
      (analysisType === "aorticValveCalcium" ? hasAortaCenterline : true) &&
      pool.labelData.loaded &&
      hasTaviResourcesReady
    );
  }, [analysisType, pool, config.aorta_id, config.tavi_aorta_id]);

  const isSegmentationReady =
    pool &&
    (analysisType === 'chamber' ||
      analysisType === 'ejectionFraction' ||
      getSegmentationAnalysisType(analysisType).every((labelName) => {
        const result =
          pool[`segmentationResult_${labelName}` as keyof typeof pool];
        return result?.loaded;
      }));


  const legacyMainRenderReady =
    isCornerstoneInitialized &&
    downloaded === 0 &&
    !isLayoutStateLoading &&
    segVolAdded &&
    centerlineRenderReady &&
    !hasTaviFatalFailure;
  const isMainRenderReady =
    analysisType === 'segmentation'
      ? isSegmentationReady && downloaded === 0 && !isLayoutStateLoading
      : isCprCrossSectionMainRenderReady(
          effectiveCprCrossSectionImageMode,
          legacyMainRenderReady,
          cprCrossSectionImageShellReady,
          requiresCprCrossSectionImageShell,
        );


  const shouldShowAnalysisLoading =
    hasSeriesSelection && !!analysisType && analysisType !== "preview";

  const rebuildTaviView = () => {
    if (!selectedSeriesId || !pool || analysisType !== "tavi") return;
    splitTaviDataWorker(
      pool,
      setBufferPool,
      selectedSeriesId,
      config,
      setData,
      aortadowncheck,
    );
    resizeEngine();
    scheduleRender("CPR", { type: "updateCPRImage" });
    scheduleRender("VR", { type: "refresh" });
  };

  const taviPostReadyRebuildRef = useRef<string>("");
  useEffect(() => {
    if (analysisType !== "tavi" || !selectedSeriesId) {
      taviPostReadyRebuildRef.current = "";
      return;
    }
    const marker = `${selectedSeriesId}:${legacyMainRenderReady ? "ready" : "not-ready"}`;
    if (!legacyMainRenderReady) {
      taviPostReadyRebuildRef.current = marker;
      return;
    }
    if (taviPostReadyRebuildRef.current === marker) return;
    taviPostReadyRebuildRef.current = marker;
    const timer = setTimeout(() => {
      rebuildTaviView();
      console.warn("[PanelContainer/tavi-post-ready-rebuild]", {
        selectedSeriesId,
      });
    }, 300);
    return () => clearTimeout(timer);
  }, [analysisType, selectedSeriesId, legacyMainRenderReady]);

  // Debug: diagnose intermittent infinite loading by printing gate states.
  const loadingDebugSignatureRef = useRef<string>("");
  useEffect(() => {
    if (!shouldShowAnalysisLoading) {
      loadingDebugSignatureRef.current = "";
      return;
    }
    const status = {
      analysisType,
      selectedSeriesId,
      cprCrossSectionImageMode,
      effectiveCprCrossSectionImageMode,
      isCornerstoneInitialized,
      downloaded,
      segVolAdded,
      isLayoutStateLoading,
      centerlineRenderReady,
      cprCrossSectionImageManifestStatus,
      cprCrossSectionImageShellReady,
      requiresCprCrossSectionImageShell,
      legacyMainRenderReady,
      hasTaviFatalFailure,
      isRetryingTavi,
      isMainRenderReady,
      shouldShowAnalysisLoading,
    };
    const signature = JSON.stringify(status);
    if (loadingDebugSignatureRef.current !== signature) {
      loadingDebugSignatureRef.current = signature;
      console.warn("[PanelContainer/loading-gate-changed]", status);
    }
  }, [
    shouldShowAnalysisLoading,
    analysisType,
    selectedSeriesId,
    isCornerstoneInitialized,
    downloaded,
    segVolAdded,
    isLayoutStateLoading,
    centerlineRenderReady,
    cprCrossSectionImageManifestStatus,
    cprCrossSectionImageShellReady,
    cprCrossSectionImageMode,
    effectiveCprCrossSectionImageMode,
    requiresCprCrossSectionImageShell,
    legacyMainRenderReady,
    hasTaviFatalFailure,
    isRetryingTavi,
    isMainRenderReady,
  ]);

  useEffect(() => {
    const shouldBlockInteraction =
      shouldShowAnalysisLoading && !isMainRenderReady && !showTaviFailureScreen;

    if (shouldBlockInteraction) {
      if (analysisViewLoadingModalIdRef.current) return;

      const id = openModal(
        <Modal
          type="page"
          close={() => undefined}
          description={<ResponseWaiting type="download" message="Loading data" />}
        />,
        false,
        undefined,
        true,
      );
      analysisViewLoadingModalIdRef.current = id;
      return;
    }

    if (!analysisViewLoadingModalIdRef.current) return;
    closeModal(analysisViewLoadingModalIdRef.current);
    analysisViewLoadingModalIdRef.current = null;
  }, [
    closeModal,
    isMainRenderReady,
    openModal,
    shouldShowAnalysisLoading,
    showTaviFailureScreen,
  ]);

  useEffect(() => {
    return () => {
      if (analysisViewLoadingModalIdRef.current) {
        closeModal(analysisViewLoadingModalIdRef.current);
        analysisViewLoadingModalIdRef.current = null;
      }
    };
  }, [closeModal]);

  // Auto-rebuild safeguard for intermittent "loading done but views not ready" in TAVI.
  useEffect(() => {
    if (analysisType !== "tavi") {
      taviViewRebuildAttemptsRef.current = 0;
      return;
    }
    if (!shouldShowAnalysisLoading || isMainRenderReady) {
      taviViewRebuildAttemptsRef.current = 0;
      return;
    }
    if (!selectedSeriesId || !pool) return;
    if (hasTaviFatalFailure || isRetryingTavi) return;
    if (downloaded !== 0 || !segVolAdded) return;

    const timer = setTimeout(() => {
      if (taviViewRebuildAttemptsRef.current >= 3) return;
      taviViewRebuildAttemptsRef.current += 1;
      rebuildTaviView();
      console.warn("[PanelContainer/tavi-auto-rebuild]", {
        attempt: taviViewRebuildAttemptsRef.current,
        selectedSeriesId,
      });
    }, 2500);

    return () => clearTimeout(timer);
  }, [
    analysisType,
    shouldShowAnalysisLoading,
    isMainRenderReady,
    selectedSeriesId,
    pool,
    hasTaviFatalFailure,
    isRetryingTavi,
    downloaded,
    segVolAdded,
  ]);

  useEffect(() => {
    if (!shouldShowAnalysisLoading || isMainRenderReady) return;
    const id = setInterval(() => {
      console.warn("[PanelContainer/loading-stuck-tick]", {
        analysisType,
        selectedSeriesId,
        cprCrossSectionImageMode,
        effectiveCprCrossSectionImageMode,
        isCornerstoneInitialized,
        downloaded,
        segVolAdded,
        isLayoutStateLoading,
        centerlineRenderReady,
        cprCrossSectionImageManifestStatus,
        cprCrossSectionImageShellReady,
        requiresCprCrossSectionImageShell,
        legacyMainRenderReady,
        hasTaviFatalFailure,
        isRetryingTavi,
      });
    }, 1000);
    return () => clearInterval(id);
  }, [
    shouldShowAnalysisLoading,
    isMainRenderReady,
    analysisType,
    selectedSeriesId,
    isCornerstoneInitialized,
    downloaded,
    segVolAdded,
    isLayoutStateLoading,
    centerlineRenderReady,
    cprCrossSectionImageManifestStatus,
    cprCrossSectionImageShellReady,
    cprCrossSectionImageMode,
    effectiveCprCrossSectionImageMode,
    requiresCprCrossSectionImageShell,
    legacyMainRenderReady,
    hasTaviFatalFailure,
    isRetryingTavi,
  ]);

  const handleRetryTaviDownload = () => {
    if (!selectedSeriesId || !pool || taviFailedKeys.length === 0) return;
    if (isRetryingTavi) return;

    setIsRetryingTavi(true);
    if (retryTransitionTimerRef.current) {
      clearTimeout(retryTransitionTimerRef.current);
      retryTransitionTimerRef.current = null;
    }

    taviFailedKeys.forEach((key) => {
      delete taviRequestAttemptsRef.current[`${selectedSeriesId}:${key}`];
    });

    setBufferPool((prev) => {
      const currentSeriesPool = prev[selectedSeriesId];
      if (!currentSeriesPool) return prev;

      const nextSeriesPool: any = { ...currentSeriesPool };
      taviFailedKeys.forEach((key) => {
        const currentItem = currentSeriesPool[key] as any;
        if (!currentItem) return;
        nextSeriesPool[key] = {
          ...currentItem,
          loaded: false,
          loading: false,
          error: false,
          retryExhausted: false,
          requestedAt: undefined,
        };
      });

      nextSeriesPool["tavi.parsed"] = {
        loaded: false,
        loading: false,
        error: false,
      };

      return {
        ...prev,
        [selectedSeriesId]: nextSeriesPool,
      };
    });

    retryTransitionTimerRef.current = setTimeout(() => {
      setIsRetryingTavi(false);
      retryTransitionTimerRef.current = null;
    }, 500);
  };

  const handleRetryTaviView = () => {
    if (!selectedSeriesId || !pool || analysisType !== "tavi") return;
    taviViewRebuildAttemptsRef.current = 0;

    // Reset transient stuck states so request loop can retry immediately.
    setBufferPool((prev) => {
      const currentSeriesPool = prev[selectedSeriesId];
      if (!currentSeriesPool) return prev;
      const nextSeriesPool: any = { ...currentSeriesPool };
      TAVI_RESOURCE_KEYS.forEach((key) => {
        const currentItem = currentSeriesPool[key] as any;
        if (!currentItem) return;
        if (currentItem.loaded) return;
        nextSeriesPool[key] = {
          ...currentItem,
          loading: false,
          error: false,
          requestedAt: undefined,
          retryExhausted: false,
        };
        delete taviRequestAttemptsRef.current[`${selectedSeriesId}:${key}`];
      });
      nextSeriesPool["tavi.parsed"] = {
        ...(currentSeriesPool["tavi.parsed"] ?? {}),
        loaded: false,
        loading: false,
        error: false,
      };
      return {
        ...prev,
        [selectedSeriesId]: nextSeriesPool,
      };
    });

    // Force-request missing resources now.
    TAVI_RESOURCE_KEYS.forEach((key) => {
      const item = (pool as any)?.[key];
      if (!item?.loaded) {
        requestCPRData(
          pool,
          setBufferPool,
          accessToken,
          selectedSeriesId,
          key,
          true,
        );
      }
    });

    rebuildTaviView();
  };

  const showFfrCaptureBar =
    analysisType === "FFR" &&
    selectedLayoutConfig.type === "ffr-capture" &&
    isMainRenderReady &&
    !showTaviFailureScreen;

  useEffect(() => {
    if (analysisType !== "FFR") {
      setFfrCaptureRequest(null);
      return;
    }
    if (!showFfrCaptureBar) return;
    if (!ffrCaptureRequest) return;
    if (!pool) return;
    if (!selectedSeriesId) return;
    if (ffrCaptureInFlightRef.current) return;

    const surfaceContext = contextPool["Surface3D"] as any;
    if (!surfaceContext) return;

    ffrCaptureInFlightRef.current = true;

    (async () => {
      try {
        setData({ isCapture: true });
        await new Promise<void>((resolve) => {
          requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
        });
        const capturedUrl = await captureSurface3D(
          surfaceContext,
          analysisType,
          pool,
        );
        if (!capturedUrl) return;

        setFfrThumbnails((prev) => {
          const base = ffrCaptureRequest.reset ? [] : prev;
          if (!ffrCaptureRequest.reset && base.length >= 10) return base;
          const next = [
            ...base,
            { id: ffrThumbIdRef.current++, url: capturedUrl },
          ];
          return next.slice(0, 10);
        });
      } catch (e) {
        console.warn("[PanelContainer/ffr-capture] failed", e);
      } finally {
        setData({ isCapture: false });
        ffrCaptureInFlightRef.current = false;
        setFfrCaptureRequest(null);
      }
    })();
  }, [
    analysisType,
    showFfrCaptureBar,
    ffrCaptureRequest,
    contextPool,
    pool,
    selectedSeriesId,
  ]);

  useEffect(() => {
    const isFfrCaptureMode =
      analysisType === "FFR" && selectedLayoutConfig.type === "ffr-capture";
    if (isFfrCaptureMode) return;

    setFfrCaptureRequest(null);
    setFfrThumbnails([]);
    ffrThumbIdRef.current = 1;
  }, [analysisType, selectedLayoutConfig.type]);

  const handleCancelFfrCapture = useCallback(() => {
    if (selectedLayoutConfig.type !== "ffr") {
      updateCurrentLayout("ffr");
    }
  }, [selectedLayoutConfig.type, updateCurrentLayout]);

  const buildFfrCapturePayload = useCallback(async () => {
    const buffers: BufferData[] = [];
    const zipFiles: { name: string; data: Uint8Array }[] = [];

    for (let i = 0; i < ffrThumbnails.length; i++) {
      const thumb = ffrThumbnails[i];
      const bufferData = await imageUrlToArrayBufferSimple(thumb.url);
      buffers.push(bufferData);

      const ext = bufferData.extension || "png";
      const index = String(i + 1).padStart(2, "0");
      const filename = `ffr_capture_${index}.${ext}`;
      zipFiles.push({
        name: filename,
        data: new Uint8Array(bufferData.buffer),
      });
    }

    const pacsData: Send_PACS_Data[] = [
      {
        buffers,
        screen_capture: true,
        capture_kind: "Report",
        series_description: "CT-FFR",
      },
    ];

    return { pacsData, zipFiles };
  }, [ffrThumbnails]);

  const downloadFfrZip = useCallback(
    async (zipFiles: { name: string; data: Uint8Array }[]) => {
      if (!zipFiles.length) return;
      const zipData = createStoredZip(zipFiles);
      const now = new Date();
      const stamp = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, "0")}${String(
        now.getDate(),
      ).padStart(2, "0")}_${String(now.getHours()).padStart(2, "0")}${String(
        now.getMinutes(),
      ).padStart(2, "0")}${String(now.getSeconds()).padStart(2, "0")}`;
      const fileName = `ffr_capture_${stamp}.zip`;

      if ((window as any)?.electron?.ipcRenderer?.saveFile) {
        const view = zipData.buffer.slice(
          zipData.byteOffset,
          zipData.byteOffset + zipData.byteLength,
        );
        await (window as any).electron.ipcRenderer.saveFile(view, fileName);
        return;
      }

      const blob = new Blob([zipData], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    },
    [],
  );

  const handleSubmitFfrCapture = useCallback(
    async ({
      sendToPacs,
      download,
    }: {
      sendToPacs: boolean;
      download: boolean;
    }) => {
      if (!sendToPacs && !download) return;
      if (!ffrThumbnails.length) return;
      if (!selectedSeriesId) return;

      try {
        const { pacsData, zipFiles } = await buildFfrCapturePayload();
        const runDownload = async () => {
          if (!download) return;
          await downloadFfrZip(zipFiles);
        };

        if (sendToPacs) {
          AutoCaptureModalOpen("fullscreenCapture", pacsData, {
            onCompleted: async () => {
              try {
                await runDownload();
              } catch (error) {
                console.warn(
                  "[PanelContainer/ffr-submit] download failed after PACS",
                  error,
                );
              }
            },
          });
          return;
        }

        await runDownload();
      } catch (error) {
        console.warn("[PanelContainer/ffr-submit] failed", error);
      }
    },
    [
      selectedSeriesId,
      ffrThumbnails.length,
      buildFfrCapturePayload,
      downloadFfrZip,
      AutoCaptureModalOpen,
    ],
  );

  const handleRemoveFfrThumbnail = useCallback((id: number) => {
    setFfrThumbnails((prev) => prev.filter((thumb) => thumb.id !== id));
  }, []);

  const handleCaptureFfrThumbnail = useCallback(() => {
    scheduleRender("panel", { type: "ffrCaptureRequest" });
  }, []);

  const Renderers = useMemo(() => {
    return (
      <>
        {selectedLayoutConfig.panelConfig.map((panelConfig, index) => {
          const slotClassName = panelClassName[index];
          const isFfrCaptureSlot =
            showFfrCaptureBar &&
            slotClassName === "bottom-left" &&
            panelConfig.panelId === "EmptyPanel";

          if (isFfrCaptureSlot) {
            return (
              <div
                key={`ffr-capture-slot-${selectedSeriesId}-${index}`}
                className={slotClassName}
              >
                <FfrCaptureBar
                  onCancel={handleCancelFfrCapture}
                  thumbnails={ffrThumbnails}
                  onRemoveThumbnail={handleRemoveFfrThumbnail}
                  onCapture={handleCaptureFfrThumbnail}
                  onSubmit={handleSubmitFfrCapture}
                />
              </div>
            );
          }

          const Renderer = getPanel(panelConfig.componentKey);
          return (
            <Renderer
              key={`${panelConfig.componentKey}-${selectedSeriesId}-${slotClassName}`}
              className={slotClassName}
              panelConfig={panelConfig}
              possibleProjection={projectionList}
              topLeftContent={topLeftContent}
              topRightContent={topRightContent}
            />
          );
        })}
      </>
    );
  }, [
    selectedLayoutConfig,
    selectedSeriesId,
    projectionList,
    panelClassName,
    showFfrCaptureBar,
    handleCancelFfrCapture,
    ffrThumbnails,
    handleRemoveFfrThumbnail,
    handleCaptureFfrThumbnail,
    handleSubmitFfrCapture,
  ]);

  return (
    <div className={styles.container} style={containerGridStyle} ref={divRef}>
      {showTaviFailureScreen ? (
        <div className={styles.fullscreenState} role="alert">
          <div className={styles.stateTitle}>
            {isRetryingTavi
              ? "Retrying TAVI data download"
              : "Unable to load TAVI data"}
          </div>
          <div className={styles.stateMessage}>
            {isRetryingTavi
              ? "Retrying download request..."
              : "Network or file download failed. Please try again."}
          </div>
          <button
            type="button"
            className={styles.retryButton}
            onClick={handleRetryTaviDownload}
            disabled={isRetryingTavi}
          >
            {isRetryingTavi ? "Retrying..." : "Retry"}
          </button>
        </div>
      ) : shouldShowAnalysisLoading && !isMainRenderReady ? (
        <div className={styles.fullscreenState} aria-live="polite">
          <div className={styles.stateTitle}>Preparing analysis view</div>
          <div className={styles.stateMessage}>
            Downloading and preparing required data. Please wait.
          </div>
          {analysisType === "tavi" && (
            <button
              type="button"
              className={styles.retryButton}
              onClick={handleRetryTaviView}
              style={{ marginTop: 16 }}
            >
              Refresh View
            </button>
          )}
        </div>
      ) : (
        Renderers
      )}
    </div>
  );
};

export default PanelContainer;
